import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { format, isBefore, startOfDay, addDays, getDay, isSameDay, parseISO } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface OpeningHour {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

interface BlockedTime {
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  type: string;
}

interface ExistingReservation {
  id: string;
  date: string;
  duration: number;
}

interface RescheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentDate: Date;
  duration: number;
  reservationId: string;
  organisationId: string;
  onSuccess?: (newDate: Date, newTime: string) => void;
}

const jsToDbDay = (jsDay: number) => (jsDay === 0 ? 6 : jsDay - 1);

const RescheduleDialog = ({ open, onOpenChange, currentDate, duration, reservationId, organisationId, onSuccess }: RescheduleDialogProps) => {
  const { t } = useLanguage();
  const currentTimeStr = format(currentDate, "HH:mm");

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(currentDate);
  const [selectedTime, setSelectedTime] = useState(currentTimeStr);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [openingHours, setOpeningHours] = useState<OpeningHour[]>([]);
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [existingReservations, setExistingReservations] = useState<ExistingReservation[]>([]);

  // Fetch real data when dialog opens
  useEffect(() => {
    if (!open || !organisationId) return;
    setSelectedDate(currentDate);
    setSelectedTime(currentTimeStr);

    const fetchData = async () => {
      setLoading(true);
      const [hoursRes, blockedRes, reservationsRes] = await Promise.all([
        supabase
          .from("opening_hours")
          .select("day_of_week, is_open, open_time, close_time")
          .eq("organisation_id", organisationId),
        supabase
          .from("blocked_times")
          .select("start_date, end_date, start_time, end_time, type")
          .eq("organisation_id", organisationId)
          .eq("active", true),
        supabase
          .from("reservations")
          .select("id, date, duration")
          .eq("organisation_id", organisationId)
          .in("status", ["pending", "confirmed"]),
      ]);
      setOpeningHours(hoursRes.data || []);
      setBlockedTimes(blockedRes.data || []);
      setExistingReservations(
        (reservationsRes.data || [])
          .filter((r: any) => r.id !== reservationId)
          .map((r: any) => ({ id: r.id, date: r.date, duration: r.duration }))
      );
      setLoading(false);
    };
    fetchData();
  }, [open, organisationId, reservationId, currentDate, currentTimeStr]);

  const closedDays = useMemo(() => {
    return openingHours.filter((h) => !h.is_open).map((h) => h.day_of_week);
  }, [openingHours]);

  const fullDayBlockedDates = useMemo(() => {
    const dates: Date[] = [];
    const parseLocalDate = (d: string) => {
      const [y, m, day] = d.split("-").map(Number);
      return new Date(y, m - 1, day);
    };
    blockedTimes.forEach((bt) => {
      if (bt.type === "datetime_range" && bt.start_time && bt.end_time && bt.end_date && bt.start_date !== bt.end_date) {
        let cur = addDays(parseLocalDate(bt.start_date), 1);
        const end = addDays(parseLocalDate(bt.end_date), -1);
        while (cur <= end) { dates.push(new Date(cur)); cur = addDays(cur, 1); }
        return;
      }
      if (bt.start_time && bt.end_time) return;
      if (bt.type === "single_day" || !bt.end_date) {
        dates.push(parseLocalDate(bt.start_date));
      } else {
        let cur = parseLocalDate(bt.start_date);
        const end = parseLocalDate(bt.end_date);
        while (cur <= end) { dates.push(new Date(cur)); cur = addDays(cur, 1); }
      }
    });
    return dates;
  }, [blockedTimes]);

  const isSlotBlocked = (date: Date, slotStart: number, slotEnd: number): boolean => {
    return blockedTimes.some((bt) => {
      if (!bt.start_time || !bt.end_time) return false;
      const [sy, sm, sd] = bt.start_date.split("-").map(Number);
      const btStartDate = new Date(sy, sm - 1, sd);
      const [ey, em, ed] = bt.end_date ? bt.end_date.split("-").map(Number) : [sy, sm, sd];
      const btEndDate = new Date(ey, em - 1, ed);
      const dateDay = startOfDay(date);
      if (dateDay < btStartDate || dateDay > btEndDate) return false;

      const [bsH, bsM] = bt.start_time!.split(":").map(Number);
      const [beH, beM] = bt.end_time!.split(":").map(Number);
      const blockStart = bsH * 60 + bsM;
      const blockEnd = beH * 60 + beM;

      if (bt.type === "datetime_range" && bt.end_date && bt.start_date !== bt.end_date) {
        const isFirstDay = dateDay.getTime() === btStartDate.getTime();
        const isLastDay = dateDay.getTime() === btEndDate.getTime();
        const isMiddleDay = dateDay > btStartDate && dateDay < btEndDate;
        if (isMiddleDay) return true;
        if (isFirstDay && isLastDay) {
          if (blockStart >= blockEnd) return true;
          return slotStart < blockEnd && slotEnd > blockStart;
        }
        if (isFirstDay) return slotEnd > blockStart;
        if (isLastDay) return slotStart < blockEnd;
        return false;
      }

      if (blockStart >= blockEnd) return false;
      return slotStart < blockEnd && slotEnd > blockStart;
    });
  };

  const isSlotBooked = (date: Date, slotStart: number, slotEnd: number): boolean => {
    return existingReservations.some((r) => {
      const rDate = parseISO(r.date);
      if (!isSameDay(rDate, date)) return false;
      const rStart = rDate.getHours() * 60 + rDate.getMinutes();
      const rEnd = rStart + r.duration;
      return slotStart < rEnd && slotEnd > rStart;
    });
  };

  const getAvailableSlots = (date: Date): string[] => {
    const dayOfWeek = jsToDbDay(getDay(date));
    const dayHours = openingHours.find((h) => h.day_of_week === dayOfWeek && h.is_open);
    if (!dayHours) return [];
    const [openH, openM] = dayHours.open_time.split(":").map(Number);
    const [closeH, closeM] = dayHours.close_time.split(":").map(Number);
    const closeMinutes = closeH * 60 + closeM;

    const slots: string[] = [];
    let m = openH * 60 + openM;
    while (m + duration <= closeMinutes) {
      const slotEnd = m + duration;
      if (!isSlotBlocked(date, m, slotEnd) && !isSlotBooked(date, m, slotEnd)) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        slots.push(`${h.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`);
      }
      m += 30;
    }
    return slots;
  };

  const isDateDisabled = (date: Date) => {
    const today = startOfDay(new Date());
    if (isBefore(date, today)) return true;
    const dow = jsToDbDay(getDay(date));
    if (closedDays.includes(dow)) return true;
    if (fullDayBlockedDates.some((d) => d.getTime() === startOfDay(date).getTime())) return true;
    if (openingHours.length > 0) {
      const slots = getAvailableSlots(date);
      if (slots.length === 0) return true;
    }
    return false;
  };

  const timeSlots = useMemo(() => {
    if (!selectedDate || openingHours.length === 0) return [];
    return getAvailableSlots(selectedDate);
  }, [selectedDate, openingHours, blockedTimes, existingReservations, duration]);

  // Auto-select first slot when date changes
  useEffect(() => {
    if (selectedDate && timeSlots.length > 0 && !timeSlots.includes(selectedTime)) {
      setSelectedTime(timeSlots[0]);
    }
  }, [selectedDate, timeSlots]);

  const hasChanged =
    selectedDate &&
    selectedTime &&
    (startOfDay(selectedDate).getTime() !== startOfDay(currentDate).getTime() || selectedTime !== currentTimeStr);

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime) return;
    setSaving(true);

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const newDateStr = `${dateStr}T${selectedTime}:00`;

    // Fetch customer email + language to use the manage-booking edge function (sends notification emails)
    const { data: resData } = await supabase
      .from("reservations")
      .select("customers(email), language")
      .eq("id", reservationId)
      .single();

    if (!resData || !(resData as any).customers?.email) {
      // Fallback: direct update without email
      const [h, m] = selectedTime.split(":").map(Number);
      const newDate = new Date(selectedDate);
      newDate.setHours(h, m, 0, 0);
      const { error } = await supabase
        .from("reservations")
        .update({ date: newDate.toISOString() })
        .eq("id", reservationId);
      setSaving(false);
      if (error) {
        toast.error(t("reservations.reschedule_failed"));
        return;
      }
      toast.success(`${t("reservations.reschedule_success")} ${format(newDate, "MMM d, yyyy")} ${t("reservations.at")} ${selectedTime}`);
      onOpenChange(false);
      window.location.reload();
      return;
    }

    const customerEmail = (resData as any).customers.email;
    const lang = (resData as any).language || "nl";

    const { data, error } = await supabase.functions.invoke("manage-booking", {
      body: {
        action: "rebook",
        reservation_id: reservationId,
        email: customerEmail,
        new_date: newDateStr,
        notify_customer: true,
        notify_organisation: false,
        language: lang,
        source: "dashboard",
      },
    });

    setSaving(false);

    if (error || data?.error) {
      toast.error(data?.error || t("reservations.reschedule_failed"));
      return;
    }

    const [h, m] = selectedTime.split(":").map(Number);
    const displayDate = new Date(selectedDate);
    displayDate.setHours(h, m, 0, 0);
    toast.success(`${t("reservations.reschedule_success")} ${format(displayDate, "MMM d, yyyy")} ${t("reservations.at")} ${selectedTime}`);
    onOpenChange(false);
    if (onSuccess) {
      onSuccess(displayDate, selectedTime);
    } else {
      window.location.reload();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("reservations.reschedule_title")}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t("reservations.reschedule_current")} {format(currentDate, "EEEE, MMM d, yyyy")} {t("reservations.at")} {currentTimeStr}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col gap-6 md:flex-row py-2">
            <div className="flex-1">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => {
                  setSelectedDate(d);
                  setSelectedTime("");
                }}
                disabled={isDateDisabled}
                weekStartsOn={1}
                className="p-3 pointer-events-auto rounded-xl border border-border"
                initialFocus
              />
            </div>

            {selectedDate && (
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-3">
                  {t("reservations.reschedule_available")} {format(selectedDate, "EEEE, MMM d")}
                </p>
                {timeSlots.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("reservations.reschedule_no_slots")}</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedTime(slot)}
                        className={cn(
                          "rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                          selectedTime === slot
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border text-foreground hover:border-primary/30 hover:bg-primary/5"
                        )}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("reservations.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={!hasChanged || saving || loading}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t("reservations.reschedule_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleDialog;
